import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Disclaimer } from "@/components/Disclaimer";
import { CelebrationMoment, useMilestones } from "@/components/CelebrationMoment";
import { isAtOrPast } from "@/lib/journey";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Home, LayoutDashboard, UserCog, ListChecks, Binoculars, Scale,
  BadgeCheck, Library, MessageCircleQuestion, LogOut, Menu, X, CalendarDays, ChevronDown, Calculator, Wrench, BookOpen, CalendarHeart,
} from "lucide-react";

const nav = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/app/onboarding", label: "My Profile", icon: UserCog, testId: "nav-onboarding" },
  { to: "/app/journey", label: "My Journey", icon: ListChecks, testId: "nav-journey" },
  { to: "/app/touring", label: "Touring Prep", icon: Binoculars, testId: "nav-touring" },
  { to: "/app/tours", label: "Tour Schedule", icon: CalendarDays, testId: "nav-tours" },
  { to: "/app/compare", label: "Compare Homes", icon: Scale, testId: "nav-compare" },
  { to: "/app/calculator", label: "Payment Calculator", icon: Calculator, testId: "nav-calculator" },
  { to: "/app/offer", label: "Offer Readiness", icon: BadgeCheck, testId: "nav-offer" },
  { to: "/app/resources", label: "Resources", icon: Library, testId: "nav-resources" },
  { to: "/app/vendors", label: "Vendor Guide", icon: Wrench, testId: "nav-vendors" },
  { to: "/app/glossary", label: "Glossary", icon: BookOpen, testId: "nav-glossary" },
  { to: "/app/anniversary", label: "Home Anniversary", icon: CalendarHeart, testId: "nav-anniversary", afterKeys: true },
  { to: "/app/assistant", label: "Ask a question", icon: MessageCircleQuestion, testId: "nav-assistant" },
];

export function BuyerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const { data: ms } = useMilestones();
  const navItems = nav.filter((n) => !n.afterKeys || (ms && isAtOrPast(ms.stage, "keys")));

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const SidebarInner = () => (
    <>
      <div className="flex items-center gap-2 px-5 h-16 border-b border-border">
        <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
          <Home className="h-4 w-4 text-primary-foreground" />
        </div>
        <span className="font-heading text-lg font-bold">Look Before You Leap 808</span>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testId}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <Button variant="ghost" onClick={doLogout} data-testid="buyer-logout-btn" className="w-full justify-start gap-3 text-muted-foreground">
          <LogOut className="h-4 w-4" /> Log out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <SidebarInner />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-card flex flex-col">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/70 backdrop-blur sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="hidden sm:block font-heading text-lg font-semibold">Your workspace</div>
          <div className="flex items-center gap-3 ml-auto">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 rounded-full hover:bg-secondary px-1.5 py-1 transition-colors outline-none" data-testid="buyer-user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-forest text-white text-xs">
                    {user?.name?.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="buyer-logout-menu-item" className="text-destructive gap-2 cursor-pointer"><LogOut className="h-4 w-4" /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-6xl w-full mx-auto">
          <Outlet />
          <CelebrationMoment />
          <div className="mt-10">
            <Disclaimer />
          </div>
        </main>
      </div>
    </div>
  );
}
